import type { CancelPending } from "./privateGameCancelJournal";
import {privateGameTransport,type PrivateGameTransport} from "./privateGamePlay.service";
import type { ApiClient } from "@/shared/api/client";
import { scopeFailure, type TravelScope } from "@/features/journey/travelPlan/currentScope";
export type InvitationStatus = "pending" | "accepted" | "declined" | "cancelled" | "expired";
import type { PrivateGameInvitationPetResponse } from "@/shared/contracts/generated";
/** Actual I5584 generated GET metadata; original write receipts stay base values. */
export type InvitationPetResponse = Readonly<PrivateGameInvitationPetResponse>;
export type PrivateInvitation = Readonly<{
  invitation_id: string; pet_id: string; rules_version: "gomoku-freestyle-v1";
  human_player_id: string; pet_player_id: string;
  origin: Readonly<{ conversation_id: string; invitation_message_id: string }>;
  created_at: string; expires_at: string; status: InvitationStatus; version: number;
  pet_response?: InvitationPetResponse | null;
}>;
export type OriginalInvitationMessage = Readonly<{ messageId: string; invitationId: string; text: string; createdAt: string }>;
function fields(value: unknown): Record<string, PropertyDescriptor> {
  if (!value || typeof value !== "object" || Array.isArray(value) || ![null, Object.prototype].includes(Object.getPrototypeOf(value))) throw scopeFailure();
  const result = Object.getOwnPropertyDescriptors(value);
  if (Reflect.ownKeys(result).some(k => typeof k !== "string")
    || Object.values(result).some(d => !("value" in d) || typeof d.value === "function")) throw scopeFailure();
  return result;
}
const string = (d: Record<string, PropertyDescriptor>, name: string): string => {
  const value = d[name]?.value;
  if (typeof value !== "string" || !value.trim()) throw scopeFailure();
  return value;
};
export function originalInvitationMessage(message: unknown): OriginalInvitationMessage | null {
  try {
    const d = fields(message), source = d.source_event_id?.value;
    if (d.sender?.value !== "owner" || d.channel?.value !== "private" || typeof source !== "string") return null;
    const match = /^game-invite:(game-invitation:[a-f0-9]{64})$/.exec(source);
    if (!match) return null;
    return Object.freeze({ messageId: string(d, "message_id"), invitationId: match[1], text: string(d, "text"), createdAt: string(d, "created_at") });
  } catch { return null; }
}
export function sameOriginalInvitation(a: OriginalInvitationMessage, message: unknown) {
  const b = originalInvitationMessage(message);
  return Boolean(b && a.messageId === b.messageId && a.invitationId === b.invitationId && a.text === b.text && a.createdAt === b.createdAt);
}
function utcResponseTime(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(?:Z|\+00:00)$/.exec(value);
  const time = Date.parse(value), date = new Date(time);
  if (!match || !Number.isFinite(time) || Number(match[1]) < 1
    || [date.getUTCFullYear(), date.getUTCMonth()+1, date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].some((part,index) => part !== Number(match[index+1]))) throw scopeFailure();
  return time;
}
function qualifiedPetResponse(d: Record<string, PropertyDescriptor>, status: string, version: number, created: string): InvitationPetResponse | null {
  if (!d.pet_response || d.pet_response.value === null) return null;
  const response = fields(d.pet_response.value), decision = string(response, "decision"), responseVersion = response.invitation_version?.value;
  if (Object.keys(response).sort().join(",") !== "decision,invitation_version,responded_at"
    || !["accept", "decline", "later"].includes(decision) || !Number.isSafeInteger(responseVersion) || responseVersion < 1) throw scopeFailure();
  const responded = string(response,"responded_at");
  if (utcResponseTime(responded) < Date.parse(created)) throw scopeFailure();
  const consistent = status === "pending" ? decision === "later" && responseVersion === version
    : status === "accepted" ? decision === "accept" && responseVersion === version
    : status === "declined" ? decision === "decline" && responseVersion === version
    : status === "cancelled" ? decision === "later" && responseVersion + 1 === version
    // GET can project expiry without a writer/version increment; historical
    // later remains history, never a current accepted state.
    : status === "expired" && decision === "later" && (responseVersion === version || responseVersion + 1 === version);
  if (!consistent) throw scopeFailure();
  return Object.freeze({decision: decision as InvitationPetResponse["decision"], invitation_version:responseVersion, responded_at:responded});
}
export function qualifiedInvitation(value: unknown, scope: TravelScope, message: OriginalInvitationMessage): PrivateInvitation {
  const d = fields(value), origin = fields(d.origin?.value);
  const status = string(d, "status"), version = d.version?.value;
  if (!["pending", "accepted", "declined", "cancelled", "expired"].includes(status)
    || !Number.isSafeInteger(version) || version < 0
    || string(d, "invitation_id") !== message.invitationId || string(d, "pet_id") !== scope.petId
    || string(d, "human_player_id") !== "user:" + scope.userId || string(d, "pet_player_id") !== "pet:" + scope.petId
    || string(d, "rules_version") !== "gomoku-freestyle-v1"
    || string(origin, "invitation_message_id") !== message.messageId) throw scopeFailure();
  const created = string(d, "created_at"), expires = string(d, "expires_at");
  if (!Number.isFinite(Date.parse(created)) || !Number.isFinite(Date.parse(expires)) || Date.parse(expires) < Date.parse(created)) throw scopeFailure();
  const petResponse = qualifiedPetResponse(d,status,version,created);
  return Object.freeze({
    invitation_id: message.invitationId, pet_id: scope.petId, rules_version: "gomoku-freestyle-v1",
    human_player_id: "user:" + scope.userId, pet_player_id: "pet:" + scope.petId,
    origin: Object.freeze({ conversation_id: string(origin, "conversation_id"), invitation_message_id: message.messageId }),
    created_at: created, expires_at: expires, status: status as InvitationStatus, version,
    ...(d.pet_response ? {pet_response:petResponse} : {}),
  });
}
export interface PrivateInvitationReader {
  play?: PrivateGameTransport;
  /** Missing exact-read capability fails closed; never scan or use cached thread. */
  originalMessage?(petId: string, messageId: string, signal: AbortSignal): Promise<unknown>;
  read(petId: string, invitationId: string, signal: AbortSignal): Promise<unknown>;
  cancel?(pending: CancelPending, signal: AbortSignal): Promise<unknown>;
  operation?(pending: CancelPending, signal: AbortSignal): Promise<unknown>;
}
export function qualifiedCancelReceipt(value: unknown, pending: CancelPending): PrivateInvitation {
  const d = fields(value);
  if (d.schema_version?.value !== 1 || d.operation_id?.value !== pending.operationId || d.submission_status?.value !== "committed"
    || d.operation?.value !== "cancel_invitation" || d.invitation_id?.value !== pending.invitationId || d.game_id?.value !== null
    || typeof d.replayed?.value !== "boolean" || d.original_receipt_version?.value !== pending.expectedVersion + 1) throw scopeFailure();
  const receipt = qualifiedInvitation(d.original_receipt?.value,pending,{invitationId:pending.invitationId,messageId:pending.messageId,text:"unused",createdAt:pending.messageCreatedAt});
  if (receipt.version !== pending.expectedVersion + 1 || !["cancelled","expired"].includes(receipt.status)
    || receipt.created_at !== pending.invitationCreatedAt || receipt.origin.conversation_id !== pending.conversationId) throw scopeFailure();
  return receipt;
}
export function originalCancelResult(value: unknown, pending: CancelPending): PrivateInvitation | null {
  const d = fields(value);
  if (d.operation_id?.value !== pending.operationId) throw scopeFailure();
  if (d.status?.value === "UNKNOWN" && d.original_result?.value === null && d.reason?.value === "original_operation_missing") return null;
  if (d.status?.value !== "COMMITTED" || d.reason?.value !== null) throw scopeFailure();
  return qualifiedCancelReceipt(d.original_result?.value,pending);
}
export const privateInvitationReader = (api: ApiClient): PrivateInvitationReader => ({
  play: privateGameTransport(api),
  originalMessage: (petId,messageId,signal) => api.request("/communicator/"+encodeURIComponent(petId)+"/messages/by-id/"+encodeURIComponent(messageId),{method:"GET",signal}),
  cancel: (pending,signal) => api.request("/communicator/"+encodeURIComponent(pending.petId)+"/games/invitations/"+encodeURIComponent(pending.invitationId)+"/cancel",{
    method:"POST",signal,idempotencyKey:pending.operationId,body:{expected_invitation_version:pending.expectedVersion,operation_id:pending.operationId},
  }),
  operation: (pending,signal) => api.request("/communicator/"+encodeURIComponent(pending.petId)+"/games/operations/"+encodeURIComponent(pending.operationId),{method:"GET",signal}),
  read: (petId, invitationId, signal) => api.request("/communicator/" + encodeURIComponent(petId) + "/games/invitations/" + encodeURIComponent(invitationId), { method: "GET", signal }),
});
